import { ButtonType, ButtonWidget, DialogType, TextLinkWidget } from '@tableau/widgets';
import { Formik, useFormikContext } from 'formik';
import * as React from 'react';
import { useSettingsCoordinator } from '~settings-coordinator/SettingsCoordinatorHook';
import { Messages } from '../../../../../codegen/Localize';
import { ModalInstance, ReactModalService } from '../../../../../commonInfra/dialogs/ReactModalService';
import { ToasterService, ToastType } from '../../../../../ts/services/ToasterService';
import WebHttpClient from '../../../../../httpClient/WebHttpClient';
import { IResult } from '../../../../../httpClient/serverApi/types/IResponse';
import { FeatureFlags } from '../../../../../ts/FeatureFlags';
import { AddOAuthClientsRequest, AddOAuthClientParams } from '../../../../../ts/ServerApi/requests/AddOAuthClientsRequest';
import { DeleteOAuthClientsRequest } from '../../../../../ts/ServerApi/requests/DeleteOAuthClientsRequest';
import { GetOAuthClientsRequest } from '../../../../../ts/ServerApi/requests/GetOAuthClientsRequest';
import { IOAuthClient } from '../../../../../ts/ServerApi/types/IOAuthClient';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { learnMoreStyle } from '../../../../styles/CommonStyles';
import { addOAuthClientDialogStyle, buttonStyle } from '../../../styles/OAuthClientsRegistryStyles';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import {
  AddOAuthClientDialog,
  AddOAuthClientDialogProps,
  AddOAuthClientDialogState,
  OAuthClientSetting,
  OAuthClientsFormField,
  OAuthClientsFormikProps
} from './AddOAuthClientDialog';

interface AddOAuthClientResult {
  id: string;
  errorMessage: string;
}
interface AddOAuthClientsResult {
  addOAuthClientsResult: AddOAuthClientResult[];
}

function getDescription(oauthClientsRegistryHelpUrl: string) {
  return <>
    <span data-tb-test-id='oauth-client-registry-description'>
      {Messages.oauthClientsRegistryDescription()}
      <TextLinkWidget
        className={learnMoreStyle}
        href={oauthClientsRegistryHelpUrl}
        target='_blank'
        testId='oauth-client-registry-learn-more'
      >
        {Messages.learnMore()}
      </TextLinkWidget>
    </span>
  </>;
}

function renderOAuthClientListTableHeader() {
  const tableHeaders = { [Messages.oauthClientDbClass()]: '', [Messages.oauthClientInstanceUrl()]: '', [Messages.oauthClientId()]: '', '': '' };
  let header = Object.keys(tableHeaders);
  return header.map((key, index) => {
    return (<th key={index} className='tb-data-grid-headers-line'>
      <span className='tb-data-grid-header-text'>{key}</span>
    </th>);
  });
}

/**
 * Component with dependencies injected as props
 */
export function OAuthClientsRegistrySection() {
  const [oauthClients, setOAuthClients] = React.useState<OAuthClientSetting[]>([]);
  const initialValues = React.useMemo(() => {
    return { oauthClients };
  }, [oauthClients]);

  React.useEffect(() => fetchOAuthClients(), []);

  function fetchOAuthClients(formik?: OAuthClientsFormikProps) {
    WebHttpClient.sendRequest(new GetOAuthClientsRequest({}))
      .then((result: { oauthClients: IOAuthClient[] }) => {
        const clientSettings = result.oauthClients.map((v: IOAuthClient) => {
          const clientSetting: OAuthClientSetting = {
            dbClass: v.type,
            instanceUrl: v.instanceUrl ?? '',
            oauthClientId: v.clientId,
            oauthClientSecret: '',
            oauthClientRedirectUri: '',
            id: v.id
          };
          return clientSetting;
        });
        setOAuthClients(clientSettings);
      });
  }

  const handleSaveAction = React.useCallback(async (values: OAuthClientsFormField, formik: OAuthClientsFormikProps) => {
    let toAddClients: AddOAuthClientParams[] = [];
    values.oauthClients
      .filter((client: OAuthClientSetting) => client.oauthClientSecret !== '')
      .map((client: OAuthClientSetting) => {
        toAddClients.push({
          'dbClass': client.dbClass,
          'instanceUrl': client.instanceUrl,
          'clientId': client.oauthClientId,
          'clientSecret': client.oauthClientSecret,
          'redirectUri': client.oauthClientRedirectUri
        });
      });

    const promises: Promise<IResult>[] = [];

    // the difference between initial id set and the current one are the ids to be deleted
    let origIds = initialValues.oauthClients.filter((client: OAuthClientSetting) => client.id !== '').map((client: OAuthClientSetting) => client.id);
    let newIds = values.oauthClients.filter((client: OAuthClientSetting) => client.id !== '').map((client: OAuthClientSetting) => client.id);
    let toDeleteIds = origIds.filter((id: string) => newIds.indexOf(id) < 0);

    if (toAddClients.length) {
      const addPromise = WebHttpClient.sendRequest(new AddOAuthClientsRequest({ oauthClients: toAddClients }));
      promises.push(addPromise);
    }
    if (toDeleteIds.length) {
      const delPromise = WebHttpClient.sendRequest(new DeleteOAuthClientsRequest(toDeleteIds));
      promises.push(delPromise);
    }

    if (promises.length) {
      try {
        const result = await Promise.all(promises);
        const addClientResults = result[0] as AddOAuthClientsResult;
        const failedAdd = addClientResults.addOAuthClientsResult?.filter((x: AddOAuthClientResult) => x.id === '-1');
        if (failedAdd?.length) {
          const errMsg = failedAdd.map(x => x.errorMessage).join('; ');
          throw [errMsg];
        }
      } finally {
        fetchOAuthClients(formik);
      }
    }
  }, [initialValues.oauthClients]);

  return (
    <SettingsSection title={Messages.oauthClientsRegistry()}
      description={getDescription('')}>
      <div>
        <Formik initialValues={initialValues}
          enableReinitialize={true}
          onSubmit={(values: OAuthClientsFormField, formik: OAuthClientsFormikProps) => handleSaveAction(values, formik)}>
          <FormContent />
        </Formik>
      </div>
    </SettingsSection>
  );
}

function useAddOAuthClientSubmit() {
  const formik = useFormikContext<OAuthClientsFormField>();
  return React.useCallback((client: AddOAuthClientDialogState) => {
    const clients = formik.values.oauthClients.filter((c: OAuthClientSetting) => (c.dbClass !== client.dbClass || c.instanceUrl !== client.instanceUrl));
    clients.push({
      dbClass: client.dbClass,
      instanceUrl: client.instanceUrl,
      oauthClientId: client.oauthClientId,
      oauthClientSecret: client.oauthClientSecret,
      oauthClientRedirectUri: client.oauthClientRedirectUri,
      id: Math.random().toString(36).substring(2)
    });
    formik.setFieldValue('oauthClients', clients, false);
  }, [formik, formik.setFieldValue]);
}

export function useOpenAddOAuthClientDialog() {
  const formik = useFormikContext<OAuthClientsFormField>();
  const handleAddOAuthClientSubmit = useAddOAuthClientSubmit();
  return React.useCallback((): Promise<IResult> => {
    let modalInstance: ModalInstance;
    const dialogProps = {
      dismiss: () => modalInstance && modalInstance.dismiss(),
      close: (result: any) => modalInstance && modalInstance.close(result),
      addOAuthClientSubmit: handleAddOAuthClientSubmit
    };

    modalInstance = ReactModalService.open({
      type: DialogType.Uber,
      dialogContent: React.createElement(AddOAuthClientDialog, dialogProps),
      testId: 'addOAuthClientDialog',
      showCloseButton: true,
      dialogStyle: addOAuthClientDialogStyle,
      shouldNotSetDefaultWidth: true
    });
    return modalInstance.result;
  }, [formik]);
}

export function FormContent() {
  useSettingsCoordinator();
  const formik = useFormikContext<OAuthClientsFormField>();
  const handleOpenAddOAuthClientDialog = useOpenAddOAuthClientDialog();

  return <>
    <div>
      <ButtonWidget buttonType={ButtonType.Outline}
                    testId='add-oauth-client-on-site-button'
                    className='tb-outline-button tb-margin-left-wide'
                    onClick={() => handleOpenAddOAuthClientDialog() }
                    disabled={false}
                    type='button'>{Messages.addOAuthClient_action()}
      </ButtonWidget>
    </div>
    <p />
    { (formik.values.oauthClients.length > 0 || formik.dirty) &&
    <div>
      <table id='oauth-clients-list-table-id' className='tb-static-grid-table tb-static-grid-table-settings-width'>
        <thead>
        <tr data-test-id='create-oauth-client-db-class'>
          {renderOAuthClientListTableHeader()}
        </tr>
        </thead>
        <tbody id='oauth-clients-list-body-id'>
          {renderOAuthClientListTableBody(formik)}
        </tbody>
      </table>
    </div>
    }
  </>;
}

export function handleDeleteClient(client: OAuthClientSetting, formik: OAuthClientsFormikProps) {
  const oauthClients = formik.values.oauthClients;
  const index = oauthClients.indexOf(client, 0);
  if (index > -1) {
    const clients = [...oauthClients];
    clients.splice(index, 1);
    formik.setFieldValue('oauthClients', clients, false);
  }
}

function renderOAuthClientListTableBody(formik: OAuthClientsFormikProps) {
  return formik.values.oauthClients.map((client: OAuthClientSetting) => {
    const { id, dbClass, instanceUrl, oauthClientId } = client;
    return (
      <tr key={id}>
        <td><span className='tb-data-grid-cell-text tb-lr-padded-wide'>{dbClass}</span></td>
        <td><span className='tb-data-grid-cell-text tb-lr-padded-wide'>{instanceUrl}</span></td>
        <td><span className='tb-data-grid-cell-text tb-lr-padded-wide'>{oauthClientId}</span></td>
        <td>
            <span className='tb-data-grid-cell-text tb-lr-padded-wide'>
              <ButtonWidget buttonType={ButtonType.LowEmphasis}
                            testId='delete-oauth-client-on-site-button'
                            className={buttonStyle}
                            onClick={() => handleDeleteClient(client, formik)}
                            disabled={false}
                            type='button'>{Messages.oauthDeleteClient()}
              </ButtonWidget>
            </span>
        </td>
      </tr>
    );
  });
}
export const OAuthClientsRegistrySectionSpecs = componentRegistryEntry({
  name: 'OAuthClientsRegistrySection',
  component: OAuthClientsRegistrySection
});
